/**
 * Carte d'une essence plantée (page projet, page entreprise) : une carte par
 * espèce, cultivars et variétés regroupés (src/lib/essences/especes.ts),
 * photo de l'espèce avec son crédit, ou illustration neutre sans photo.
 */
import Image from 'next/image'
import type { EssenceAffichee } from '@/lib/essences/especes'

function Illustration() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-sage/40" aria-hidden="true">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-forest/50">
        <path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 1c1 2 2 4.5 1 8-1 3.5-1.5 5.5-4 8.5" />
        <path d="M2 21c0-3 1.85-5.36 5.08-6" />
      </svg>
    </div>
  )
}

/**
 * Aucune anecdote du catalogue des essences : sans source, elles sont
 * retirées partout, site public et espace contributeur (08/10/2026).
 */
export function CarteEssence({ essence, isFr }: { essence: EssenceAffichee; isFr: boolean }) {
  const { name, data, latin, photo } = essence
  const sous = [latin, data?.famille].filter(Boolean).join(' · ')
  return (
    <div className="rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        <figure className="relative w-full sm:w-48 flex-shrink-0 m-0">
          <div className="relative w-full h-44 sm:h-36">
            {photo ? (
              <Image src={photo.src} alt={isFr ? `${name}, photo de l'espèce` : `${name}, species photo`} fill className="object-cover" sizes="(max-width: 640px) 100vw, 192px" />
            ) : (
              <Illustration />
            )}
          </div>
          {photo && (
            <figcaption className="px-2 py-1 text-[10px] leading-snug text-gray-400">
              <a href={photo.source} target="_blank" rel="noopener noreferrer license" className="hover:underline">{photo.credit}</a>
            </figcaption>
          )}
        </figure>
        <div className="p-4 flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div>
              <h4 className="font-heading font-bold text-gray-900 text-sm">{name}</h4>
              {sous && <p className="text-xs text-gray-500 italic">{sous}</p>}
            </div>
            {data && (
              <span className="text-xs bg-sage text-forest px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                {data.type === 'résineux' ? (isFr ? 'Résineux' : 'Conifer') : (isFr ? 'Feuillu' : 'Deciduous')}
              </span>
            )}
          </div>
          {data?.description && <p className="text-gray-600 text-xs leading-relaxed mt-2">{data.description}</p>}
        </div>
      </div>
    </div>
  )
}
